/*
 * Rechenlogik des Tour-Overlays (Design-Guide `tour-overlay.md`, Musterkatalog M-017): reine Funktionen ohne DOM-Zugriff
 * (außer `leseRechteck` und `scrolleInsBild`), damit Platzierung und Klemme testbar sind. Der Baustein setzt die Ergebnisse
 * zur Laufzeit am Element (wie `dropdown-menue.tsx`); es sind Koordinaten, keine Gestaltung.
 *
 * Platzierung: bevorzugt rechts vom Ziel, sonst unten, oben, links; passt keine Seite ganz, gewinnt die mit dem meisten Platz.
 * Immer an den Viewport geklemmt. Unter 768 px (`MOBIL_BREITE`) gilt das Bottom-Sheet: keine Koordinaten, das Ziel wird in
 * die obere Bildhälfte gescrollt.
 */

export interface Rechteck {
  links: number;
  oben: number;
  breite: number;
  hoehe: number;
}

export interface Bildschirm {
  breite: number;
  hoehe: number;
}

export type Seite = "rechts" | "unten" | "oben" | "links" | "mitte";

export interface Platzierung {
  seite: Seite;
  links: number;
  oben: number;
}

/** Breite, unter der das Popover zum Bottom-Sheet wird (Tailwind `md`). */
export const MOBIL_BREITE = 768;
/** Mindestabstand zum Bildschirmrand. */
export const RAND = 8;
/** Abstand zwischen Ziel-Ring und Popover. */
export const ABSTAND = 12;
/** Luft zwischen Ziel und Ring/Loch. */
export const LOCH_LUFT = 6;

export function istMobil(bs: Bildschirm): boolean {
  return bs.breite < MOBIL_BREITE;
}

/** Rechteck eines Elements (Fensterkoordinaten). */
export function leseRechteck(el: Element): Rechteck {
  const r = el.getBoundingClientRect();
  return { links: r.left, oben: r.top, breite: r.width, hoehe: r.height };
}

const zwischen = (wert: number, min: number, max: number): number => Math.max(min, Math.min(wert, max));

/**
 * Loch im Dimmer: Ziel plus Luft, an den Bildschirm geklemmt (ein Ziel, das größer ist als der Bildschirm, bekommt ein
 * Loch in Bildschirmgröße statt einer negativen oder überstehenden Fläche).
 */
export function lochUmZiel(ziel: Rechteck, bs: Bildschirm): Rechteck {
  const links = zwischen(ziel.links - LOCH_LUFT, 0, bs.breite);
  const oben = zwischen(ziel.oben - LOCH_LUFT, 0, bs.hoehe);
  const rechts = zwischen(ziel.links + ziel.breite + LOCH_LUFT, 0, bs.breite);
  const unten = zwischen(ziel.oben + ziel.hoehe + LOCH_LUFT, 0, bs.hoehe);
  return { links, oben, breite: Math.max(0, rechts - links), hoehe: Math.max(0, unten - oben) };
}

/**
 * Vier Dimm-Flächen um das Loch (oben, unten, links, rechts). Kein `box-shadow` mit riesigem Streuradius: die Fläche IM Loch
 * bleibt unbedeckt, das Ziel bleibt bedienbar. Ohne Loch eine Vollfläche.
 */
export function dimmerFlaechen(loch: Rechteck | null, bs: Bildschirm): Rechteck[] {
  if (!loch) return [{ links: 0, oben: 0, breite: bs.breite, hoehe: bs.hoehe }];
  const rechtsStart = loch.links + loch.breite;
  const untenStart = loch.oben + loch.hoehe;
  return [
    { links: 0, oben: 0, breite: bs.breite, hoehe: loch.oben },
    { links: 0, oben: untenStart, breite: bs.breite, hoehe: Math.max(0, bs.hoehe - untenStart) },
    { links: 0, oben: loch.oben, breite: loch.links, hoehe: loch.hoehe },
    { links: rechtsStart, oben: loch.oben, breite: Math.max(0, bs.breite - rechtsStart), hoehe: loch.hoehe },
  ];
}

/** Platzierung des Popovers (Desktop). Ohne Ziel mittig. */
export function platziere(ziel: Rechteck | null, groesse: { breite: number; hoehe: number }, bs: Bildschirm): Platzierung {
  const maxLinks = Math.max(RAND, bs.breite - groesse.breite - RAND);
  const maxOben = Math.max(RAND, bs.hoehe - groesse.hoehe - RAND);
  if (!ziel) {
    return {
      seite: "mitte",
      links: zwischen((bs.breite - groesse.breite) / 2, RAND, maxLinks),
      oben: zwischen((bs.hoehe - groesse.hoehe) / 2, RAND, maxOben),
    };
  }
  const platz = {
    rechts: bs.breite - (ziel.links + ziel.breite) - ABSTAND - RAND,
    unten: bs.hoehe - (ziel.oben + ziel.hoehe) - ABSTAND - RAND,
    oben: ziel.oben - ABSTAND - RAND,
    links: ziel.links - ABSTAND - RAND,
  };
  const passt = {
    rechts: platz.rechts >= groesse.breite && bs.hoehe - 2 * RAND >= groesse.hoehe,
    unten: platz.unten >= groesse.hoehe && bs.breite - 2 * RAND >= groesse.breite,
    oben: platz.oben >= groesse.hoehe && bs.breite - 2 * RAND >= groesse.breite,
    links: platz.links >= groesse.breite && bs.hoehe - 2 * RAND >= groesse.hoehe,
  };
  const reihenfolge: Array<"rechts" | "unten" | "oben" | "links"> = ["rechts", "unten", "oben", "links"];
  let seite = reihenfolge.find((s) => passt[s]);
  if (!seite) {
    // Nichts passt ganz (z. B. ein Ziel über die ganze Fläche): die Seite mit dem meisten Platz, der Rest wird geklemmt.
    seite = reihenfolge.reduce((beste, s) => (platz[s] > platz[beste] ? s : beste), "rechts" as "rechts" | "unten" | "oben" | "links");
  }
  let links: number;
  let oben: number;
  if (seite === "rechts") {
    links = ziel.links + ziel.breite + ABSTAND;
    oben = ziel.oben;
  } else if (seite === "links") {
    links = ziel.links - ABSTAND - groesse.breite;
    oben = ziel.oben;
  } else if (seite === "unten") {
    links = ziel.links;
    oben = ziel.oben + ziel.hoehe + ABSTAND;
  } else {
    links = ziel.links;
    oben = ziel.oben - ABSTAND - groesse.hoehe;
  }
  return { seite, links: zwischen(links, RAND, maxLinks), oben: zwischen(oben, RAND, maxOben) };
}

/**
 * Liegt das Ziel so im Bild, dass nichts gescrollt werden muss? Mobil muss es zusätzlich in der oberen Bildhälfte enden,
 * weil das Bottom-Sheet die untere Hälfte belegt. Ein Ziel, das höher ist als der Platz, zählt als sichtbar, sobald sein
 * Anfang im Bild liegt (Scrollen würde es nicht besser machen).
 */
export function istImSichtbereich(ziel: Rechteck, bs: Bildschirm, mobil: boolean): boolean {
  const grenzeUnten = mobil ? bs.hoehe * 0.5 : bs.hoehe - RAND;
  const unterkante = ziel.oben + ziel.hoehe;
  const anfangImBild = ziel.oben >= RAND && ziel.oben < grenzeUnten;
  if (ziel.hoehe > grenzeUnten - RAND) return anfangImBild;
  return ziel.oben >= RAND && unterkante <= grenzeUnten;
}

/**
 * Holt das Ziel ins Bild (nächster scrollender Vorfahre, auch innerhalb von Dialogen). Mobil an den Anfang, damit es in der
 * oberen Hälfte liegt; sonst in die Mitte. `reduziert` = Nutzerwunsch „weniger Bewegung“: ohne weiches Scrollen.
 * Gibt zurück, ob gescrollt wurde.
 */
export function scrolleInsBild(el: Element, bs: Bildschirm, reduziert: boolean): boolean {
  const mobil = istMobil(bs);
  if (istImSichtbereich(leseRechteck(el), bs, mobil)) return false;
  el.scrollIntoView({ behavior: reduziert ? "auto" : "smooth", block: mobil ? "start" : "center", inline: "nearest" });
  return true;
}
